import {
  HalfFloatType,
  LinearSRGBColorSpace,
  NoToneMapping,
  RenderTarget as ThreeRenderTarget,
  UnsignedByteType,
  WebGPURenderer,
  type Color as ThreeColor,
} from "three/webgpu";
import { Color as ThreeColorClass } from "three/webgpu";
import type { Color } from "../common/color.js";

/** Thrown when neither WebGPU nor WebGL 2 is available, or the renderer cannot start. */
export class GpuUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GpuUnavailableError";
  }
}

export interface GpuContextOptions {
  /** Called when the GPU device is lost; the caller should rebuild resources. */
  onDeviceLost?: (info: { reason: string | null; message: string }) => void;
  /** Called after a resize changes the backing store, before the next frame. */
  onResize?: (size: CanvasSize) => void;
  /**
   * Anisotropic filtering for smooth textures, 1 to `MAX_ANISOTROPY` (16, the default). Lower is blurrier on
   * surfaces seen at a grazing angle and lighter on texture bandwidth, which a weak or mobile GPU feels.
   */
  maxAnisotropy?: number;
  /** Use three's WebGL 2 backend even where WebGPU is available - a player's choice, or for testing the fallback. */
  forceWebGL?: boolean;
  /**
   * Which GPU to ask the browser for where there is more than one: `"high-performance"` (the default) is usually
   * the discrete card, `"low-power"` the integrated one, which runs cooler and saves a laptop's battery. A hint the
   * browser may ignore - it never makes start-up fail. Fixed for the context's life.
   */
  powerPreference?: GPUPowerPreference;
}

/** The graphics API every frame is drawn with: WebGPU, or three's WebGL 2 fallback. */
export type GraphicsApi = "WebGPU" | "WebGL 2";

/**
 * What the browser says about the GPU it handed over, as far as it will say: browsers keep most of it back from a
 * page, so any field may be empty.
 */
export interface GpuAdapterSummary {
  readonly api: GraphicsApi;
  readonly vendor: string;
  readonly architecture: string;
  readonly device: string;
  readonly description: string;
  /** A software adapter (SwiftShader and the like), which draws on the CPU. */
  readonly fallback: boolean;
  /** What `GpuContextOptions.powerPreference` asked for. */
  readonly powerPreference: GPUPowerPreference;
}

/** The adapter in a few words for a settings screen or a diagnostics line: "WebGPU · nvidia ampere". */
export function describeAdapter(adapter: GpuAdapterSummary): string {
  const name = adapter.description.trim() || [adapter.vendor, adapter.architecture].map((part) => part.trim()).filter(Boolean).join(" ");
  const parts = [adapter.api, name || "unknown GPU"];
  if (adapter.fallback) parts.push("software");
  return parts.join(" · ");
}

/**
 * `GPUAdapterInfo`, or what WebGL's debug extension says, read defensively: older browsers have no `info`, fields
 * may be missing, and WebGL's renderer string stands in for a description.
 */
export function summarizeAdapter(
  api: GraphicsApi,
  info: Partial<GPUAdapterInfo> | undefined,
  powerPreference: GPUPowerPreference,
  fallback = false,
): GpuAdapterSummary {
  const text = (value: unknown): string => (typeof value === "string" ? value : "");
  const flagged = (info as { isFallbackAdapter?: unknown } | undefined)?.isFallbackAdapter;
  const description = text(info?.description);
  return {
    api,
    vendor: text(info?.vendor),
    architecture: text(info?.architecture),
    device: text(info?.device),
    description,
    fallback: flagged === true || fallback || /swiftshader|llvmpipe|software/i.test(description),
    powerPreference,
  };
}

/** What WebGL will say of its GPU: the unmasked strings where the debug extension is offered. */
function webGlInfo(gl: WebGL2RenderingContext | undefined): Partial<GPUAdapterInfo> | undefined {
  if (!gl) return undefined;
  const debug = gl.getExtension("WEBGL_debug_renderer_info");
  const vendor = gl.getParameter(debug ? debug.UNMASKED_VENDOR_WEBGL : gl.VENDOR) as unknown;
  const renderer = gl.getParameter(debug ? debug.UNMASKED_RENDERER_WEBGL : gl.RENDERER) as unknown;
  return {
    vendor: typeof vendor === "string" ? vendor : "",
    description: typeof renderer === "string" ? renderer : "",
  } as Partial<GPUAdapterInfo>;
}

export interface CanvasSize {
  /** CSS pixels. */
  width: number;
  height: number;
  /** Device pixels (width * dpr, rounded). */
  pixelWidth: number;
  pixelHeight: number;
  dpr: number;
}

/**
 * How a texture addresses coordinates outside 0..1.
 *
 * `clamp` holds the edge pixel, which is what a sprite, a glyph atlas or a render target wants. `repeat`
 * tiles, which is what a surface unwrapped so that u runs past 1 needs.
 */
export type TextureWrap = "clamp" | "repeat";

/**
 * Samples taken along the long axis of a pixel's texture footprint. 16 is the ceiling every desktop GPU of
 * the last fifteen years implements and the value a driver clamps down from where it cannot.
 */
export const MAX_ANISOTROPY = 16;

/** `GpuContextOptions.maxAnisotropy` made whole: a whole number from 1 to `MAX_ANISOTROPY`, the most by default. */
export function anisotropyOf(asked: number | undefined): number {
  return asked === undefined || !Number.isFinite(asked) ? MAX_ANISOTROPY : Math.min(MAX_ANISOTROPY, Math.max(1, Math.round(asked)));
}

/**
 * A canvas context whose `configure` survives a platform that refuses a copyable swapchain.
 *
 * three asks for `COPY_SRC` on the canvas texture unconditionally, and some platforms - Android's compositor
 * among them - cannot hand one out; `configure` then throws and the renderer never starts. Webwin captures
 * by rendering into a target rather than reading the swapchain, so the usage is not needed and is dropped
 * on refusal.
 */
function forgivingContext(canvas: HTMLCanvasElement): GPUCanvasContext | undefined {
  const context = canvas.getContext("webgpu");
  if (!context) return undefined;
  const configure = context.configure.bind(context);
  context.configure = (configuration: GPUCanvasConfiguration): undefined => {
    try {
      configure(configuration);
    } catch (err) {
      console.warn("webwin: this device cannot copy from the swapchain; configuring without it.", err);
      configure({ ...configuration, usage: GPUTextureUsage.RENDER_ATTACHMENT });
    }
    return undefined;
  };
  return context;
}

/**
 * Owns the three.js renderer and the canvas it draws on. Everything downstream - the game's scenes, the UI's
 * quad batches, off-screen targets - renders through `renderer`.
 *
 * The renderer runs **without color management** (linear output, no tone mapping): values written are the
 * values shown. The UI is authored in display values and blends in display space, as a 2D canvas does, and a
 * lit 3D scene does its own linear-to-display conversion as the last step of its pipeline.
 */
export class GpuContext {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: WebGPURenderer;
  readonly maxAnisotropy: number;
  /** The GPU the browser handed over, as far as it says - see `GpuAdapterSummary` and `describeAdapter`. */
  readonly adapter: GpuAdapterSummary;

  #size: CanvasSize;
  #resizeObserver: ResizeObserver | null = null;
  #dprMedia: MediaQueryList | null = null;
  #options: GpuContextOptions;
  #disposed = false;
  #capture: ThreeRenderTarget | null = null;
  #clear = new ThreeColorClass();

  private constructor(canvas: HTMLCanvasElement, renderer: WebGPURenderer, options: GpuContextOptions) {
    this.canvas = canvas;
    this.renderer = renderer;
    this.#options = options;
    this.maxAnisotropy = Math.min(anisotropyOf(options.maxAnisotropy), renderer.getMaxAnisotropy() || 1);
    this.adapter = this.#readAdapter(options.powerPreference ?? "high-performance");
    this.#size = measure(canvas);
    this.#applySize(this.#size, true);
  }

  static async create(canvas: HTMLCanvasElement, options: GpuContextOptions = {}): Promise<GpuContext> {
    const wantWebGpu = !options.forceWebGL && typeof navigator !== "undefined" && "gpu" in navigator && !!navigator.gpu;
    let renderer: WebGPURenderer | null = null;
    let lastError: unknown = null;
    const powerPreference = options.powerPreference ?? "high-performance";
    for (const forceWebGL of wantWebGpu ? [false, true] : [true]) {
      try {
        // three's WebGL backend asks for its context without a power preference, so it is asked for here, with
        // the attributes three would have used.
        const context = forceWebGL
          ? (canvas.getContext("webgl2", { antialias: false, alpha: true, depth: true, stencil: false, powerPreference }) ?? undefined)
          : forgivingContext(canvas);
        const candidate = new WebGPURenderer({
          canvas,
          context,
          alpha: true,
          premultipliedAlpha: true,
          antialias: false,
          forceWebGL,
          powerPreference,
        } as ConstructorParameters<typeof WebGPURenderer>[0]);
        await candidate.init();
        renderer = candidate;
        break;
      } catch (err) {
        lastError = err;
      }
    }
    if (!renderer) {
      throw new GpuUnavailableError(`No usable GPU backend (WebGPU or WebGL 2): ${String(lastError)}`);
    }
    renderer.outputColorSpace = LinearSRGBColorSpace;
    renderer.toneMapping = NoToneMapping;
    renderer.autoClear = false;
    // Free until a light casts one.
    renderer.shadowMap.enabled = true;
    renderer.setPixelRatio(1);
    renderer.onDeviceLost = (info: { api: string; message: string; reason: string | null }) => {
      console.error("webwin: GPU device lost -", info.reason, info.message);
      options.onDeviceLost?.({ reason: info.reason, message: info.message });
    };

    const gpu = new GpuContext(canvas, renderer, options);
    gpu.#watchSize();
    return gpu;
  }

  get size(): CanvasSize {
    return this.#size;
  }

  /** True on the WebGPU backend, false where three fell back to WebGL 2. */
  get isWebGPU(): boolean {
    return (this.renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend === true;
  }

  /** The raw device on the WebGPU backend, for diagnostics; null under WebGL. */
  get device(): GPUDevice | null {
    return this.isWebGPU ? ((this.renderer.backend as unknown as { device: GPUDevice }).device ?? null) : null;
  }

  #readAdapter(powerPreference: GPUPowerPreference): GpuAdapterSummary {
    try {
      if (this.isWebGPU) {
        const info = (this.device as (GPUDevice & { adapterInfo?: GPUAdapterInfo }) | null)?.adapterInfo;
        return summarizeAdapter("WebGPU", info, powerPreference);
      }
      const gl = (this.renderer.backend as unknown as { gl?: WebGL2RenderingContext }).gl;
      return summarizeAdapter("WebGL 2", webGlInfo(gl), powerPreference);
    } catch {
      return summarizeAdapter(this.isWebGPU ? "WebGPU" : "WebGL 2", undefined, powerPreference);
    }
  }

  /** Capture renders into a target and reads that back, so it works on every platform. */
  get canCapture(): boolean {
    return true;
  }

  /** Re-measure the canvas; returns true if the backing store changed. */
  syncSize(): boolean {
    const next = measure(this.canvas);
    if (next.pixelWidth === this.#size.pixelWidth && next.pixelHeight === this.#size.pixelHeight && next.dpr === this.#size.dpr) {
      return false;
    }
    this.#applySize(next, false);
    return true;
  }

  #applySize(size: CanvasSize, initial: boolean): void {
    this.#size = size;
    this.renderer.setDrawingBufferSize(size.pixelWidth, size.pixelHeight, 1);
    if (!initial) this.#options.onResize?.(size);
  }

  #watchSize(): void {
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.syncSize()) this.#options.onResize?.(this.#size);
    });
    this.#resizeObserver.observe(this.canvas);
    this.#listenForDprChange();
  }

  // devicePixelRatio changes (monitor swap, browser zoom) do not fire ResizeObserver
  // when the CSS size is unchanged, so watch it explicitly.
  #listenForDprChange(): void {
    this.#dprMedia?.removeEventListener("change", this.#onDprChange);
    this.#dprMedia = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    this.#dprMedia.addEventListener("change", this.#onDprChange, { once: true });
  }

  #onDprChange = (): void => {
    if (this.#disposed) return;
    if (this.syncSize()) this.#options.onResize?.(this.#size);
    this.#listenForDprChange();
  };

  /**
   * Point the renderer at `target` (the canvas when null) and clear it to `clear`. Everything rendered until
   * the next call lands on top, in order.
   */
  beginFrame(clear: Color | null, target: ThreeRenderTarget | null = null): void {
    const renderer = this.renderer;
    renderer.setRenderTarget(target);
    if (clear) {
      renderer.setClearColor(this.#clear.setRGB(clear.r, clear.g, clear.b, LinearSRGBColorSpace), clear.a);
      // three's WebGL backend clears the canvas with a bare `gl.clear`, which lands on whatever framebuffer
      // is still bound - the last target a viewport rendered into, wiping it. Bind the canvas first, through
      // three's own state cache so it does not lose track.
      if (!target && !this.isWebGPU) {
        const backend = renderer.backend as unknown as { gl: WebGL2RenderingContext; state: { bindFramebuffer(t: number, f: null): void } };
        backend.state.bindFramebuffer(backend.gl.FRAMEBUFFER, null);
      }
      renderer.clear(true, true, true);
    }
  }

  /** The current clear color as three wants it - scratch, do not keep. */
  threeColor(c: Color): ThreeColor {
    return this.#clear.setRGB(c.r, c.g, c.b, LinearSRGBColorSpace);
  }

  /**
   * A frame-sized target for `Desktop.capture`: draw the frame into it with `beginFrame(clear, target)`, then
   * `readTarget` it. Rendering again rather than reading the swapchain means a capture is of a frame you
   * chose, works on platforms with no copyable swapchain, and is not tied to the task the frame ran in.
   */
  captureTarget(): ThreeRenderTarget {
    const { pixelWidth, pixelHeight } = this.#size;
    if (!this.#capture) {
      this.#capture = new ThreeRenderTarget(pixelWidth, pixelHeight, { type: UnsignedByteType, depthBuffer: true });
    } else if (this.#capture.width !== pixelWidth || this.#capture.height !== pixelHeight) {
      this.#capture.setSize(pixelWidth, pixelHeight);
    }
    return this.#capture;
  }

  /** RGBA8, top row first, from a target rendered this frame. */
  async readTarget(target: ThreeRenderTarget): Promise<{ width: number; height: number; data: Uint8Array }> {
    const { width, height } = target;
    const raw = (await this.renderer.readRenderTargetPixelsAsync(target, 0, 0, width, height)) as ArrayBufferView;
    const bytes = new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength);
    const rowBytes = width * 4;
    // WebGPU pads every row but the last to 256 bytes; WebGL reads bottom row first, unpadded.
    const padded = Math.ceil(rowBytes / 256) * 256;
    const stride = this.isWebGPU && bytes.length >= padded * (height - 1) + rowBytes ? padded : rowBytes;
    const data = new Uint8Array(rowBytes * height);
    for (let y = 0; y < height; y++) {
      const from = this.isWebGPU ? y : height - 1 - y;
      data.set(bytes.subarray(from * stride, from * stride + rowBytes), y * rowBytes);
    }
    return { width, height, data };
  }

  dispose(): void {
    this.#disposed = true;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#dprMedia?.removeEventListener("change", this.#onDprChange);
    this.#dprMedia = null;
    this.#capture?.dispose();
    this.renderer.dispose();
  }
}

/** HalfFloat, for a target a lit scene accumulates light in before it is brought to display values. */
export const LINEAR_TARGET_TYPE = HalfFloatType;

function measure(canvas: HTMLCanvasElement): CanvasSize {
  const dpr = window.devicePixelRatio || 1;
  // clientWidth is 0 for a detached canvas; fall back to the attribute size.
  const width = canvas.clientWidth || canvas.width || 1;
  const height = canvas.clientHeight || canvas.height || 1;
  return {
    width,
    height,
    pixelWidth: Math.max(1, Math.round(width * dpr)),
    pixelHeight: Math.max(1, Math.round(height * dpr)),
    dpr,
  };
}
